import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTeamStore } from '@/stores/teamStore';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { PAGE_REGISTRY } from '@/navigation/page-registry';
import {
  canAccessAndrewOnlyTabs,
  isAndrewOnlyTab,
  isPageAllowed,
} from '@/navigation/access';

interface NavItem {
  id: string;
  label: string;
  icon: JSX.Element;
  requiresIntegration?:
    | 'gorgias'
    | 'shopify'
    | 'cloudinary'
    | 'gtm'
    | 'bigquery'
    | 'klaviyo'
    | 'kodif'
    | 'sage'
    | 'flip';
}

interface NavSection {
  id: string;
  label: string;
  abbrev: string; // Short label for collapsed state
  icon: JSX.Element;
  items: NavItem[];
}

const PERSONAL_NAV_ITEMS: NavItem[] = [
  {
    id: 'daily-ritual',
    label: 'Daily',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    ),
  },
  {
    id: 'learning',
    label: PAGE_REGISTRY.learning.label,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        <path d="M8 7h8" />
        <path d="M8 11h6" />
        <path d="M8 15h4" />
      </svg>
    ),
  },
  {
    id: 'boards',
    label: 'Boards',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="5" height="18" rx="1" />
        <rect x="9.5" y="8" width="5" height="13" rx="1" />
        <rect x="16" y="13" width="5" height="8" rx="1" />
      </svg>
    ),
  },
  {
    id: 'approvals',
    label: 'Approvals',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
];

const NAV_SECTIONS: NavSection[] = [
  {
    id: 'home',
    label: 'Home',
    abbrev: 'HOME',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'cx',
    label: 'CX',
    abbrev: 'CX',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    items: [
      {
        id: 'cx-home',
        label: 'CX Home',
        requiresIntegration: 'gorgias',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
            <path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z" />
          </svg>
        ),
      },
      {
        id: 'okendo',
        label: 'Okendo Reviews',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
      },
      {
        id: 'gorgias',
        label: 'Gorgias',
        requiresIntegration: 'gorgias',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
          </svg>
        ),
      },
      {
        id: 'kodif',
        label: 'Kodif',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <path d="M9 9h6" />
            <path d="M9 13h4" />
          </svg>
        ),
      },
      {
        id: 'flip',
        label: 'Flip CX',
        requiresIntegration: 'flip',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        ),
      },
      {
        id: 'trustpilot',
        label: 'Trustpilot',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
      },
      {
        id: 'google-business',
        label: 'Google Reviews',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ),
      },
      {
        id: 'chargebacks',
        label: 'Chargebacks',
        requiresIntegration: 'shopify',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    abbrev: 'MKTG',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
    items: [
      {
        id: 'cloudinary',
        label: 'Cloudinary',
        requiresIntegration: 'cloudinary',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        ),
      },
      {
        id: 'studio',
        label: 'Studio',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="14" height="12" rx="2" />
            <path d="M7 20h14a1 1 0 0 0 1-1V9" />
            <path d="M8 10h.01" />
            <path d="M17 13l-3-3a2 2 0 0 0-2.8 0L5 16" />
            <path d="M17 7l4 3-4 3V7z" />
          </svg>
        ),
      },
      {
        id: 'email',
        label: PAGE_REGISTRY.email.label,
        requiresIntegration: PAGE_REGISTRY.email.requiresIntegration,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'ecommerce',
    label: 'Commercial',
    abbrev: 'COMM',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2l3 7h10l3 0" />
        <path d="M5 7H3" />
        <path d="M7 13h13l-1.5 5h-10z" />
        <circle cx="10" cy="20" r="1.5" />
        <circle cx="17" cy="20" r="1.5" />
      </svg>
    ),
    items: [
      {
        id: 'dogfooding',
        label: 'Dogfooding',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'gtm',
    label: 'Commercial',
    abbrev: 'COMM',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    items: [
      {
        id: 'roadshow-rep-performance',
        label: 'Rep Performance',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 3 3 5-6" />
          </svg>
        ),
      },
      {
        id: 'pricing',
        label: 'Product Pricing',
        requiresIntegration: 'shopify',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
        ),
      },
      {
        id: 'forward-coverage',
        label: 'Forward Coverage',
        requiresIntegration: 'bigquery',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
        ),
      },
      {
        id: 'replenishment',
        label: 'Replenishment',
        requiresIntegration: 'bigquery',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        ),
      },
      {
        id: 'pricing-optimizer',
        label: 'Pricing Optimizer',
        requiresIntegration: 'shopify',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
          </svg>
        ),
      },
      {
        id: 'inventory-audit',
        label: 'Inventory Audit',
        requiresIntegration: 'shopify',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <path d="M3.27 6.96L12 12.01l8.73-5.05" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
            <path d="M7.5 4.21l9 5.2" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'product',
    label: 'Merch & Product',
    abbrev: 'M&P',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    items: [
      {
        id: 'target-tna',
        label: PAGE_REGISTRY['target-tna'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ),
      },
      {
        id: 'production-wip',
        label: PAGE_REGISTRY['production-wip'].label,
        requiresIntegration: PAGE_REGISTRY['production-wip'].requiresIntegration,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M8 4v16" />
            <path d="M16 4v16" />
            <path d="M3 10h18" />
            <path d="M3 15h18" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'leadership',
    label: 'Executive',
    abbrev: 'EXEC',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    items: [
      {
        id: 'exec-pulse',
        label: PAGE_REGISTRY['exec-pulse'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18" />
            <path d="M7 15l4-4 3 3 5-7" />
            <path d="M14 7h5v5" />
          </svg>
        ),
      },
      {
        id: 'exec-dashboards',
        label: PAGE_REGISTRY['exec-dashboards'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          </svg>
        ),
      },
      {
        id: 'team-priorities',
        label: PAGE_REGISTRY['team-priorities'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="4" rx="1" />
            <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
            <path d="M9 12h6" />
            <path d="M9 16h6" />
          </svg>
        ),
      },
      {
        id: 'netsuite-master-plan',
        label: PAGE_REGISTRY['netsuite-master-plan'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            <path d="M8 7h8" />
            <path d="M8 11h8" />
            <path d="M8 15h5" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    abbrev: 'OPS',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    items: [
      {
        id: 'fulfillment',
        label: 'Fulfillment',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" />
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </svg>
        ),
      },
      {
        id: 'operations-inventory',
        label: 'Inventory Shortage',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        ),
      },
      {
        id: 'operations-oos-queue',
        label: 'OOS Queue',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        ),
      },
      {
        id: '3pl-audit',
        label: '3PL Audit',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3h18v4H3z" />
            <path d="M3 10h18v4H3z" />
            <path d="M3 17h18v4H3z" />
          </svg>
        ),
      },
      {
        id: 'split-ship',
        label: 'Split Ship',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 3h5v5" />
            <path d="M8 3H3v5" />
            <path d="M21 3l-7 7" />
            <path d="M3 3l7 7" />
            <path d="M16 21h5v-5" />
            <path d="M8 21H3v-5" />
            <path d="M21 21l-7-7" />
            <path d="M3 21l7-7" />
          </svg>
        ),
      },
      {
        id: 'operations-cancellations',
        // Pulled from the registry so the label cannot drift, the way
        // operations-inventory's has.
        label: PAGE_REGISTRY['operations-cancellations'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'wholesale',
    label: 'Operations',
    abbrev: 'OPS',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <rect x="9" y="13" width="6" height="8" />
      </svg>
    ),
    items: [
      {
        id: 'milestones',
        label: 'Milestones',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        ),
      },
      {
        id: 'roadshows',
        label: 'Roadshows',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        ),
      },
      {
        id: 'planograms',
        label: 'Planograms',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 9v12" />
            <path d="M15 9v12" />
          </svg>
        ),
      },
      {
        id: 'shipbob-orders',
        label: 'Wholesale Order Tracker',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" />
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'engineering',
    label: 'Transformation',
    abbrev: 'XFORM',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    items: [
      {
        id: 'ai-os',
        label: PAGE_REGISTRY['ai-os'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 21V9" />
          </svg>
        ),
      },
      {
        id: 'workflows',
        label: 'Automations',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        ),
      },
      {
        id: 'github',
        label: 'GitHub',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
          </svg>
        ),
      },
      {
        id: 'agents',
        label: 'Agents',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="4" width="16" height="12" rx="2" />
            <path d="M8 20h8" />
            <path d="M12 16v4" />
            <circle cx="9" cy="10" r="1" />
            <circle cx="15" cy="10" r="1" />
          </svg>
        ),
      },
      {
        id: 'skills',
        label: 'Skills',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
            <path d="M8 7h8" />
            <path d="M8 11h6" />
          </svg>
        ),
      },
      {
        id: 'mill-deployments',
        label: PAGE_REGISTRY['mill-deployments'].label,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
        ),
      },
      {
        id: 'notion-ai-tasks',
        label: 'Notion AI Tasks',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16v16H4z" />
            <path d="M8 8h8" />
            <path d="M8 12h8" />
            <path d="M8 16h5" />
          </svg>
        ),
      },
      {
        id: 'transformation-dashboard',
        label: 'AI Command Centre',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 3 3 5-6" />
          </svg>
        ),
      },
    ],
  },
];

const SECTION_CANONICAL: Record<string, Pick<NavSection, 'id' | 'label' | 'abbrev'>> = {
  ecommerce: { id: 'commercial', label: 'Commercial', abbrev: 'COMM' },
  gtm: { id: 'commercial', label: 'Commercial', abbrev: 'COMM' },
  leadership: { id: 'executive', label: 'Executive', abbrev: 'EXEC' },
  product: { id: 'merch-product', label: 'Merch & Product', abbrev: 'M&P' },
  wholesale: { id: 'operations', label: 'Operations', abbrev: 'OPS' },
  engineering: { id: 'transformation', label: 'Transformation', abbrev: 'XFORM' },
};

const DEPARTMENT_SORT_ORDER = [
  'commercial',
  'cx',
  'executive',
  'finance',
  'legal',
  'marketing',
  'merch-product',
  'operations',
  'transformation',
];

const TRUE_STORIES_PLAYLIST_EMBED =
  'https://www.youtube.com/embed/videoseries?list=PL6RxxEFgwYygOpwXzHiAptzQyYm4kDxIr';

const FINANCE_SECTION: NavSection = {
  id: 'finance',
  label: 'Finance',
  abbrev: 'FIN',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  items: [],
};

const LEGAL_SECTION: NavSection = {
  id: 'legal',
  label: 'Legal',
  abbrev: 'LEGAL',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18" />
      <path d="M5 6h14" />
      <path d="M6 6l-4 7h8L6 6z" />
      <path d="M18 6l-4 7h8l-4-7z" />
      <path d="M8 21h8" />
    </svg>
  ),
  items: [],
};

function canonicalizeSection(section: NavSection): NavSection {
  const canonical = SECTION_CANONICAL[section.id];
  return canonical ? { ...section, ...canonical } : section;
}

function departmentSortIndex(section: NavSection): number {
  const index = DEPARTMENT_SORT_ORDER.indexOf(section.id);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function sortDepartmentSections(a: NavSection, b: NavSection): number {
  const orderDelta = departmentSortIndex(a) - departmentSortIndex(b);
  return orderDelta || a.label.localeCompare(b.label);
}

function firstName(value: string | null | undefined): string {
  return (value || '').trim().split(/\s+/)[0] || 'User';
}

function AndrewOnlyIndicator() {
  return (
    <span
      className="sidebar__visibility-indicator"
      aria-label="Andrew-only"
      title="Andrew-only"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </span>
  );
}

interface SidebarProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export function Sidebar({ activeTab, onTabChange }: SidebarProps) {
  const currentTeam = useTeamStore((state) => state.currentTeam);
  const currentUserName = useAuthStore((state) => state.user?.name);
  const currentUserEmail = useAuthStore((state) => state.user?.email);
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const setSidebarCollapsed = useUIStore((state) => state.setSidebarCollapsed);
  const expandedSections = useUIStore((state) => state.expandedSections);
  const toggleSection = useUIStore((state) => state.toggleSection);
  const lastAutoExpandedSectionRef = useRef<string | null>(null);
  const personalSection = useMemo((): NavSection => ({
    id: 'personal',
    label: firstName(currentUserName),
    abbrev: firstName(currentUserName).slice(0, 4).toUpperCase(),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    items: PERSONAL_NAV_ITEMS,
  }), [currentUserName]);
  const canViewAndrewOnlyTabs = canAccessAndrewOnlyTabs(currentUserEmail);
  const currentUser = useAuthStore((state) => state.user);

  const isItemVisible = useCallback((item: NavItem): boolean => {
    if (!canViewAndrewOnlyTabs && isAndrewOnlyTab(item.id)) return false;
    // Both rules apply. This one is served by the API rather than hardcoded, so a
    // restricted account is shown exactly the pages the middleware will permit.
    if (!isPageAllowed(currentUser, item.id)) return false;
    if (!item.requiresIntegration) return true;
    if (!currentTeam) return false;

    switch (item.requiresIntegration) {
      case 'gorgias':
        return currentTeam.gorgias_configured;
      case 'shopify':
        return currentTeam.shopify_configured;
      case 'cloudinary':
        return currentTeam.cloudinary_configured;
      case 'gtm':
        return currentTeam.gtm_configured;
      case 'bigquery':
        return currentTeam.bigquery_configured;
      case 'klaviyo':
        return currentTeam.klaviyo_configured;
      case 'kodif':
        return currentTeam.kodif_configured;
      case 'sage':
        return currentTeam.sage_configured;
      case 'flip':
        return currentTeam.flip_configured;
      default:
        return true;
    }
  }, [canViewAndrewOnlyTabs, currentTeam, currentUser]);

  const visibleSections = useMemo(() => {
    const base = NAV_SECTIONS
      .map((section) => ({
        ...section,
        items: section.items.filter(isItemVisible),
      }));

    const homeSection = base.find((section) => section.id === 'home');
    const departmentsById = new Map<string, NavSection>();

    [...base.filter((section) => section.id !== 'home'), FINANCE_SECTION, LEGAL_SECTION].forEach((section) => {
      const canonical = canonicalizeSection(section);
      const existing = departmentsById.get(canonical.id);
      if (existing) {
        departmentsById.set(canonical.id, {
          ...existing,
          items: [...existing.items, ...canonical.items],
        });
      } else {
        departmentsById.set(canonical.id, canonical);
      }
    });

    const departmentSections = Array.from(departmentsById.values()).sort(sortDepartmentSections);

    const visiblePersonalSection = {
      ...personalSection,
      items: personalSection.items.filter(isItemVisible),
    };

    return [
      ...(homeSection ? [homeSection] : []),
      visiblePersonalSection,
      ...departmentSections,
      // A section whose every item was filtered out would otherwise render as a
      // bare header. Harmless for an unrestricted user (nothing empties), but a
      // restricted account would see a column of labels leading nowhere.
    ].filter((section) => section.items.length > 0);
  }, [isItemVisible, personalSection]);

  // Find which section contains the active tab
  const activeSectionId = useMemo(() => {
    for (const section of visibleSections) {
      if (section.items.some((item) => item.id === activeTab)) {
        return section.id;
      }
    }
    return null;
  }, [activeTab, visibleSections]);

  // Auto-expand the section containing the active tab when the active route changes.
  useEffect(() => {
    if (!activeSectionId) {
      lastAutoExpandedSectionRef.current = null;
      return;
    }

    if (lastAutoExpandedSectionRef.current === activeSectionId) {
      return;
    }

    lastAutoExpandedSectionRef.current = activeSectionId;

    if (expandedSections[activeSectionId] !== true) {
      toggleSection(activeSectionId);
    }
  }, [activeSectionId, expandedSections, toggleSection]);

  return (
    <nav
      className={`sidebar ${sidebarCollapsed ? 'sidebar--collapsed' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <button
        className="sidebar__toggle"
        onClick={toggleSidebar}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg className="sidebar__toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {visibleSections.map((section) => {
        const isSingleItem = section.id === 'home' && section.items.length === 1;
        const isExpanded = expandedSections[section.id] === true;
        const isSectionActive = section.items.some((item) => item.id === activeTab);
        const isActive = isSingleItem && isSectionActive;

        // Single-item sections render as a direct navigation link
        if (isSingleItem) {
          return (
            <div key={section.id}>
              <button
                className={`sidebar__section-header sidebar__section-header--direct ${isActive ? 'sidebar__section-header--active' : ''}`}
                onClick={() => {
                  if (sidebarCollapsed) {
                    setSidebarCollapsed(false);
                  }
                  onTabChange(section.items[0].id);
                }}
                aria-current={isActive ? 'page' : undefined}
                title={section.label}
              >
                <span className="sidebar__icon sidebar__section-icon">{section.icon}</span>
                <span className="sidebar__section-label">{section.label}</span>
                <span className="sidebar__section-abbrev">{section.abbrev}</span>
              </button>
            </div>
          );
        }

        return (
          <div key={section.id}>
            <button
              className={`sidebar__section-header ${isSectionActive ? 'sidebar__section-header--active' : ''}`}
              onClick={() => {
                if (sidebarCollapsed) {
                  setSidebarCollapsed(false);
                  if (!isExpanded) {
                    toggleSection(section.id);
                  }
                  return;
                }
                toggleSection(section.id);
              }}
              aria-expanded={isExpanded}
              title={section.label}
            >
              <span className="sidebar__icon sidebar__section-icon">{section.icon}</span>
              <span className="sidebar__section-label">{section.label}</span>
              <span className="sidebar__section-abbrev">{section.abbrev}</span>
              <svg
                className={`sidebar__section-chevron ${isExpanded ? 'sidebar__section-chevron--expanded' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            <div className={`sidebar__items ${!isExpanded || sidebarCollapsed ? 'sidebar__items--collapsed' : ''}`}>
              {section.items.map((item) => {
                const showItemVisibility = canViewAndrewOnlyTabs && isAndrewOnlyTab(item.id);
                return (
                  <button
                    key={item.id}
                    className={`sidebar__item ${activeTab === item.id ? 'sidebar__item--active' : ''}`}
                    onClick={() => {
                      if (sidebarCollapsed) {
                        setSidebarCollapsed(false);
                      }
                      onTabChange(item.id);
                    }}
                    aria-current={activeTab === item.id ? 'page' : undefined}
                    title={item.label}
                  >
                    <span className="sidebar__icon">{item.icon}</span>
                    <span className="sidebar__label">{item.label}</span>
                    {showItemVisibility && <AndrewOnlyIndicator />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="sidebar__playlist" aria-label="True Stories playlist">
        <iframe
          className="sidebar__playlist-frame"
          src={TRUE_STORIES_PLAYLIST_EMBED}
          title="True Stories playlist"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </nav>
  );
}

export default Sidebar;
